"use client";

import React, { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";

const courses = [
{
  id: "guitar",
  emoji: "🎸",
  title: "Guitar",
  tag: "Most Popular",
  tagColor: "yellow",
  description:
  "From beginner chords to advanced lead techniques. Learn acoustic, electric, and bass guitar with professional instructors who perform live.",
  features: ["Beginner to Advanced", "Acoustic & Electric", "Chord Theory", "Live Performance Training"],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_137c3c731-1766966555707.png",
  imageAlt: "Student learning guitar at Hi-Hats music academy Rajanukunte",
  duration: "45 min / session",
  level: "All Levels"
},
{
  id: "vocals",
  emoji: "🎤",
  title: "Vocals",
  tag: "Stage Ready",
  tagColor: "blue",
  description:
  "Develop your voice, technique, and confidence. Indian classical, Western contemporary, and Bollywood vocal training with stage performance focus.",
  features: ["Breath Control", "Pitch & Tone", "Indian & Western", "Stage Confidence"],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c19f9052-1772041649565.png",
  imageAlt: "Vocal training session at Hi-Hats music academy Bengaluru",
  duration: "45 min / session",
  level: "All Levels"
},
{
  id: "art",
  emoji: "🎨",
  title: "Art & Craft",
  tag: "Creative",
  tagColor: "yellow",
  description:
  "Explore painting, sketching, mixed media, and craft projects. Our art program nurtures visual creativity alongside musical expression.",
  features: ["Sketching & Drawing", "Watercolor & Acrylic", "Mixed Media", "Portfolio Building"],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19907e467-1769253813373.png",
  imageAlt: "Art and craft class at Hi-Hats creative academy Rajanukunte",
  duration: "60 min / session",
  level: "Kids to Adults"
},
{
  id: "jamming",
  emoji: "🥁",
  title: "Jamming Sessions",
  tag: "Signature",
  tagColor: "blue",
  description:
  "Our flagship experience. Students from different instruments come together for weekly jam sessions — the ultimate test of musical growth.",
  features: ["Multi-Instrument", "Weekly Sessions", "Live Setups", "Performance Ready"],
  image: "https://images.unsplash.com/photo-1689928002194-5dab8b35be09",
  imageAlt: "Group jamming session with multiple instruments at Hi-Hats",
  duration: "90 min / session",
  level: "Intermediate+"
}];


export default function CoursesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState("guitar");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal-hidden").forEach((el) => {
              el.classList.add("revealed");
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  const activeCourse = courses?.find((c) => c?.id === activeTab) || courses?.[0];

  return (
    <section
      id="courses"
      ref={sectionRef}
      className="py-24 md:py-32 relative"
      style={{
        background:
        "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(41,171,226,0.05) 0%, transparent 60%), #111111"
      }}>
      
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden">
          <span className="badge-yellow px-3 py-1 rounded-full text-xs font-display font-bold uppercase tracking-widest">
            What We Teach
          </span>
          <h2
            className="font-display font-black mt-4 leading-tight tracking-tight"
            style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}>
            
            Pick Your <span className="text-yellow">Instrument.</span>{" "}
            <br className="hidden md:block" />
            Find Your Sound.
          </h2>
          <p
            className="mt-4 max-w-xl mx-auto text-base md:text-lg leading-relaxed"
            style={{ color: "rgba(255,255,255,0.55)" }}>
            
            Four dedicated programs, one vibrant community. Every course is designed to take you
            from first note to first performance.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12 reveal-hidden">
          {courses?.map((c) =>
          <button
            key={c?.id}
            onClick={() => setActiveTab(c?.id)}
            className="font-display font-bold text-sm px-6 py-3 rounded-full transition-all duration-300"
            style={{
              background: activeTab === c?.id ? "#F2C94C" : "rgba(255,255,255,0.05)",
              color: activeTab === c?.id ? "#111111" : "rgba(255,255,255,0.6)",
              border: activeTab === c?.id ? "1px solid #F2C94C" : "1px solid rgba(255,255,255,0.1)",
              boxShadow: activeTab === c?.id ? "0 0 20px rgba(242,201,76,0.3)" : "none",
              transform: activeTab === c?.id ? "scale(1.05)" : "scale(1)"
            }}>
            
              {c?.emoji} {c?.title}
            </button>
          )}
        </div>

        {/* Active Course Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center reveal-hidden">
          {/* Image */}
          <div
            className="gallery-item rounded-3xl overflow-hidden relative"
            style={{ height: "380px" }}>
            
            <AppImage
              src={activeCourse?.image}
              alt={activeCourse?.imageAlt}
              className="w-full h-full object-cover" />
            
            <div
              className="absolute inset-0"
              style={{
                background:
                "linear-gradient(to top, rgba(17,17,17,0.8) 0%, transparent 60%)"
              }} />
            
            {/* Course tag */}
            <div className="absolute top-4 left-4">
              <span
                className={`${activeCourse?.tagColor === "yellow" ? "badge-yellow" : "badge-blue"} px-3 py-1 rounded-full text-xs font-display font-bold uppercase tracking-widest`}>
                
                {activeCourse?.tag}
              </span>
            </div>
            {/* Duration & Level */}
            <div className="absolute bottom-4 left-4 right-4 flex gap-3">
              <span
                className="px-3 py-1 rounded-full text-xs font-display font-semibold"
                style={{ background: "rgba(17,17,17,0.85)", border: "1px solid rgba(255,255,255,0.15)" }}>
                
                ⏱ {activeCourse?.duration}
              </span>
              <span
                className="px-3 py-1 rounded-full text-xs font-display font-semibold"
                style={{ background: "rgba(17,17,17,0.85)", border: "1px solid rgba(255,255,255,0.15)" }}>
                
                🎯 {activeCourse?.level}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6">
            <div>
              <div className="text-5xl mb-3">{activeCourse?.emoji}</div>
              <h3
                className="font-display font-black tracking-tight"
                style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)" }}>
                
                {activeCourse?.title}
              </h3>
              <p
                className="mt-3 text-base leading-relaxed"
                style={{ color: "rgba(255,255,255,0.6)" }}>
                
                {activeCourse?.description}
              </p>
            </div>

            {/* Features */}
            <div className="grid grid-cols-2 gap-3">
              {activeCourse?.features?.map((f) =>
              <div
                key={f}
                className="flex items-center gap-2 text-sm font-display font-semibold"
                style={{ color: "rgba(255,255,255,0.75)" }}>
                
                  <span className="text-yellow">✓</span>
                  {f}
                </div>
              )}
            </div>

            <a
              href="#enroll"
              className="btn-yellow inline-block px-8 py-4 rounded-xl text-sm font-display font-black tracking-wide">
              
              Enroll in {activeCourse?.title} →
            </a>
          </div>
        </div>

        {/* All Courses Grid (Mini Cards) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16">
          {courses?.map((c, i) =>
          <button
            key={c?.id}
            onClick={() => setActiveTab(c?.id)}
            className={`course-card reveal-hidden stagger-${i + 1} rounded-2xl p-6 text-left cursor-pointer w-full`}
            style={{
              borderColor: activeTab === c?.id ? "rgba(242,201,76,0.4)" : undefined,
              boxShadow: activeTab === c?.id ? "0 0 30px rgba(242,201,76,0.1)" : undefined
            }}>
            
              <div className="text-3xl mb-3">{c?.emoji}</div>
              <h4 className="font-display font-bold text-base text-white">{c?.title}</h4>
              <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.4)" }}>
                {c?.level}
              </p>
            </button>
          )}
        </div>
      </div>
    </section>);

}